import {
  parseAsArrayOf,
  parseAsBoolean,
  parseAsJson,
  parseAsString,
  parseAsStringLiteral,
} from 'nuqs';

import { projectTimeWindowValues } from './project-plan-window';
import { PROJECT_WORK_ORDER_STATUS_FILTERS } from './project-status-filter';
import { type ProjectPlanningListItem, projectStatusValues } from './types';

/** nuqs parsers for project planning list URL search params. */
export const projectSearchParsers = {
  /** Free text search across project name and factory name */
  q: parseAsString,
  /** Filter by project status (draft, released) */
  status: parseAsStringLiteral(projectStatusValues),
};

/**
 * nuqs parsers shared by the project Redraft / Release confirmation
 * sub-routes. The planner toolbar's "Redraft N work orders" /
 * "Release N work orders" buttons pass the eligible subset via this `ids`
 * param; the stepper's whole-project links drop it.
 */
export const projectActionSearchParsers = {
  /** Comma-separated work_order_ids; empty array = whole-project flow. */
  ids: parseAsArrayOf(parseAsString).withDefault([]),
};

/**
 * AG Grid's filter model is `Record<columnId, unknown>`. We treat it as
 * opaque JSON — the grid ignores malformed entries via setFilterModel, so we
 * only need to guard against non-object URL values.
 */
type ProjectFilterModel = Record<string, unknown>;

const parseProjectFilterModel = (v: unknown): ProjectFilterModel | null => {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return null;
  return v as ProjectFilterModel;
};

/** Time Window state shared by the project-plan controls and editor row scope. */
export const projectPlanSearchParsers = {
  /** Project Plan table + summary scope. Everything deliberately includes undated work orders. */
  time_window: parseAsStringLiteral(projectTimeWindowValues).withDefault('everything'),
  /** Calendar anchor for the selected Time Window. Null while viewing Everything. */
  period: parseAsString,
};

/** nuqs parsers for the planning grid (single-project) page. */
export const projectEditorSearchParsers = {
  filters: parseAsJson(parseProjectFilterModel).withDefault({}),
  ...projectPlanSearchParsers,
  /**
   * Band 2's work-order status multi-select (ENG-720). Empty = show every
   * loaded status, drafts included — see `matchesProjectStatusFilter`. Named
   * `wo_status` rather than `status` so it can't collide with the project
   * *list* page's `status` param in a shared link.
   */
  wo_status: parseAsArrayOf(parseAsStringLiteral(PROJECT_WORK_ORDER_STATUS_FILTERS)).withDefault(
    [],
  ),
  /**
   * When true, the planner fetches + shows archived work orders (greyed,
   * read-only) so they can be selected and unarchived. Drives the
   * `include_archived` query param on the detail fetch.
   */
  show_archived: parseAsBoolean.withDefault(false),
};

/**
 * Client-side filter on already-fetched project list items.
 * Matches text on project_name or any included factory name (case insensitive),
 * and optionally filters by status.
 */
export function filterProjects(
  items: ProjectPlanningListItem[],
  q: string | null,
  status: string | null,
): ProjectPlanningListItem[] {
  return items.filter((p) => {
    if (status && p.project_status !== status) return false;

    if (q) {
      const lower = q.toLowerCase();
      const nameMatch = p.project_name.toLowerCase().includes(lower);
      const codeMatch = p.project_code.toLowerCase().includes(lower);
      const factoryMatch = p.factory_names.some((name) => name.toLowerCase().includes(lower));
      if (!nameMatch && !codeMatch && !factoryMatch) return false;
    }

    return true;
  });
}
